import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { CollegeLogo } from '../../components/CollegeSearchCombobox';
import { FIT_META, deriveStats } from '../../lib/collegeFit';

/* ─── Constants / helpers ───────────────────────────────────── */

const PLAN_LABELS = {
  ED: 'Early Decision',
  ED_II: 'Early Decision II',
  EA: 'Early Action',
  REA: 'Restrictive Early Action',
  RD: 'Regular Decision',
  ROLLING: 'Rolling',
  UCAS: 'UCAS Main',
  OTHER: 'Other',
};

const PLAN_SHORT = {
  ED: 'ED',
  ED_II: 'ED II',
  EA: 'EA',
  REA: 'REA',
  RD: 'RD',
  ROLLING: 'Rolling',
  UCAS: 'UCAS',
  OTHER: 'Other',
};

const STAGES = [
  { key: 'NOT_STARTED', label: 'Not started' },
  { key: 'ESSAYS', label: 'Essays' },
  { key: 'SUBMITTED', label: 'Submitted' },
  { key: 'DECISION', label: 'Decision' },
];

const VISIT_TYPE_LABELS = {
  CAMPUS_TOUR: 'Campus Tour',
  INFO_SESSION: 'Info Session',
  INTERVIEW: 'Interview',
  OPEN_DAY: 'Open Day',
  VIRTUAL: 'Virtual Visit',
  OTHER: 'Other',
};

const dateShort = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const fmtPercent = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);
const fmtMoneyShort = (v) => (v == null ? '—' : `$${Math.round(v / 1000)}k`);
const fmtMoneyFull = (v) => (v == null ? '—' : `$${Math.round(v).toLocaleString()}`);
const fmtCount = (v) => (v == null ? '—' : Number(v).toLocaleString());

function essayStatusMeta(status) {
  if (status === 'SUBMITTED') return { label: 'done', color: '#3E6B44' };
  if (status === 'BRAINSTORMING') return { label: 'not started', color: '#A03123' };
  return { label: 'drafting', color: '#8A5F0E' }; // DRAFTING, REVISING, POLISHING
}

const linkButtonStyle = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: 12,
  color: 'var(--college-ink-soft)',
  cursor: 'pointer',
  textDecoration: 'underline',
};

const primaryButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  background: 'var(--college-ink)',
  color: 'var(--college-panel)',
  border: 'none',
  padding: '10px 20px',
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.02em',
  cursor: 'pointer',
};

/* ─── Small presentational pieces ───────────────────────────── */

function FitBadge({ fit, fitColor, fitBg }) {
  if (!fit) {
    return <span style={{ fontSize: 11, color: 'var(--college-muted-2)' }}>Not enough data yet</span>;
  }
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '4px 12px',
        borderRadius: 999,
        background: fitBg,
        color: fitColor,
        whiteSpace: 'nowrap',
      }}
    >
      {fit}
    </span>
  );
}

function StatCell({ label, value }) {
  return (
    <div style={{ background: 'var(--college-panel)', padding: 16 }}>
      <p className="label-editorial" style={{ margin: '0 0 6px', fontSize: 10 }}>{label}</p>
      <p className="font-mono-editorial" style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--college-ink)' }}>
        {value}
      </p>
    </div>
  );
}

function RailSection({ title, children }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <div style={{ borderBottom: '2px solid var(--college-ink)', paddingBottom: 8, marginBottom: 14 }}>
        <p className="label-editorial" style={{ margin: 0 }}>{title}</p>
      </div>
      {children}
    </div>
  );
}

// A horizontal score bar: an optional shaded range (the school's middle 50%)
// plus one or more labeled vertical ticks (the student's own number, and —
// for the IB band — the standard offer too).
function ScoreBar({ min, max, ticks, rangeLow, rangeHigh, marks }) {
  const pct = (v) => Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100));
  const anchorFor = (p) => (p < 15 ? 'left' : p > 85 ? 'right' : 'center');
  const translateFor = (anchor) => (anchor === 'left' ? '0%' : anchor === 'right' ? '-100%' : '-50%');

  return (
    <div style={{ margin: '30px 0 10px' }}>
      <div style={{ position: 'relative', height: 14, background: 'var(--college-border-soft)' }}>
        {rangeLow != null && rangeHigh != null && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${pct(rangeLow)}%`,
              width: `${Math.max(0, pct(rangeHigh) - pct(rangeLow))}%`,
              background: 'rgba(22,19,13,0.16)',
            }}
          />
        )}
        {marks.filter((m) => m.value != null).map((m, i) => {
          const p = pct(m.value);
          const anchor = anchorFor(p);
          return (
            <React.Fragment key={i}>
              <div
                style={{
                  position: 'absolute',
                  top: -4,
                  bottom: -4,
                  left: `${p}%`,
                  width: 3,
                  background: m.color || 'var(--college-red)',
                  transform: 'translateX(-1.5px)',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: `${p}%`,
                  transform: `translateX(${translateFor(anchor)})`,
                  marginBottom: 6,
                  whiteSpace: 'nowrap',
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--college-ink)',
                }}
              >
                {m.label}
              </span>
            </React.Fragment>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        {ticks.map((t) => (
          <span key={t} className="font-mono-editorial" style={{ fontSize: 11, color: 'var(--college-muted)' }}>
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────── */

export default function CollegeDetailPage() {
  const { id } = useParams();
  const { api } = useAuth();

  const [application, setApplication] = useState(null);
  const [latestVisit, setLatestVisit] = useState(null);
  const [essays, setEssays] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [fitReadLoading, setFitReadLoading] = useState(false);
  const [fitReadError, setFitReadError] = useState(null);
  const [advancing, setAdvancing] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    Promise.all([api.get(`/college-applications/${id}`), api.get('/student-profile')])
      .then(([detailRes, profileRes]) => {
        if (!alive) return;
        setApplication(detailRes.data.application);
        setLatestVisit(detailRes.data.latestVisit || null);
        setEssays(detailRes.data.essays || []);
        setProfile(profileRes.data.profile || null);
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(err.response?.status === 404 ? 'not-found' : 'error');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [api, id]);

  useEffect(() => { load(); }, [load]);

  const handleGenerateFitRead = async (force = false) => {
    setFitReadLoading(true);
    setFitReadError(null);
    try {
      const res = await api.post(`/college-applications/${id}/fit-read${force ? '?force=true' : ''}`);
      const { aiFitRead, fit } = res.data;
      const meta = fit ? FIT_META[fit] : null;
      setApplication((prev) => (prev ? {
        ...prev,
        aiFitRead,
        aiFitReadStale: false,
        fit,
        fitColor: meta?.color ?? null,
        fitBg: meta?.bg ?? null,
        fitPin: meta?.pin ?? null,
      } : prev));
      toast.success(force ? 'Fit read regenerated' : 'Fit read generated');
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setFitReadError({ type: 'profile', message: err.response?.data?.error });
      } else if (status === 503) {
        setFitReadError({ type: 'gemini', message: err.response?.data?.error });
      } else {
        toast.error(err.response?.data?.error || 'Failed to generate fit read');
      }
    } finally {
      setFitReadLoading(false);
    }
  };

  const handleAdvanceStage = async () => {
    if (!application) return;
    const currentIndex = Math.max(0, STAGES.findIndex((s) => s.key === application.stage));
    const next = STAGES[(currentIndex + 1) % STAGES.length];
    setAdvancing(true);
    try {
      const res = await api.put(`/college-applications/${id}`, { stage: next.key });
      setApplication(res.data.application);
      toast.success(`Marked as ${next.label}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setAdvancing(false);
    }
  };

  const backLink = (
    <Link
      to="/college/list"
      style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--college-muted)', textDecoration: 'none' }}
    >
      ← My List
    </Link>
  );

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
        {backLink}
        <p className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-muted)', marginTop: 24 }}>
          Loading…
        </p>
      </div>
    );
  }

  if (loadError || !application) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
        {backLink}
        <p className="font-serif-editorial" style={{ fontSize: 32, fontStyle: 'italic', margin: '24px 0 8px' }}>
          {loadError === 'not-found' ? "This application couldn't be found" : 'Something went wrong loading this school'}
        </p>
        <Link to="/college/list" style={{ fontSize: 13, color: 'var(--college-ink)', textDecoration: 'underline' }}>
          Back to My List →
        </Link>
      </div>
    );
  }

  const sd = application.scorecardData || {};
  const manual = application.manualStats || {};
  const stats = deriveStats(application); // { isUK, sat25, sat75, admitRate, ibOffer }

  const hasSatFields = sd.satMath25 != null || sd.satReading25 != null || sd.satComposite75 != null ||
    sd.satAverage != null || sd.satMath75 != null || sd.satReading75 != null;
  const isUS = application.country === 'US' || hasSatFields;
  const showIBBand = !isUS && stats.ibOffer != null;

  const type = sd.ownershipLabel || manual.type || null;
  const enrollment = sd.enrollmentSize ?? manual.enrollment ?? null;

  const planLabel = application.planLabel || PLAN_LABELS[application.plan] || application.plan;
  const planShort = PLAN_SHORT[application.plan] || application.plan;

  const currentStageIndex = Math.max(0, STAGES.findIndex((s) => s.key === application.stage));
  const doneEssays = essays.filter((e) => e.status === 'SUBMITTED').length;

  const costRows = [];
  if (isUS) {
    if (sd.tuitionOutOfState != null) costRows.push({ label: 'Tuition (out-of-state)', value: fmtMoneyFull(sd.tuitionOutOfState) });
    if (sd.tuitionInState != null) costRows.push({ label: 'Tuition (in-state)', value: fmtMoneyFull(sd.tuitionInState) });
    if (sd.medianDebt != null) costRows.push({ label: 'Median debt at grad', value: fmtMoneyFull(sd.medianDebt) });
    if (sd.medianEarnings10yr != null) costRows.push({ label: 'Median earnings (10yr)', value: fmtMoneyFull(sd.medianEarnings10yr) });
  } else {
    if (manual.costPerYear != null) costRows.push({ label: 'Cost per year', value: fmtMoneyFull(manual.costPerYear) });
    if (manual.tuitionNotes) costRows.push({ label: 'Tuition notes', value: manual.tuitionNotes });
    if (manual.aidNotes) costRows.push({ label: 'Financial aid notes', value: manual.aidNotes });
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
      <style>{`
        @keyframes collegeDetailSpin { to { transform: rotate(360deg); } }
        .college-spinner {
          display: inline-block; width: 12px; height: 12px;
          border: 2px solid rgba(245,240,230,0.35); border-top-color: #F5F0E6;
          border-radius: 50%; animation: collegeDetailSpin 0.7s linear infinite;
        }
        @media (max-width: 900px) {
          .college-detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {backLink}

      <div
        className="college-detail-grid animate-fade-up"
        style={{ display: 'grid', gridTemplateColumns: '8fr 4fr', gap: 48, marginTop: 24, alignItems: 'start' }}
      >
        {/* ─── LEFT COLUMN ─────────────────────────────────── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
            <CollegeLogo schoolUrl={application.schoolUrl} name={application.collegeName} size={44} />
            <h1
              className="font-serif-editorial"
              style={{ fontSize: 56, fontStyle: 'italic', fontWeight: 400, margin: 0, lineHeight: 1.05 }}
            >
              {application.collegeName}
            </h1>
          </div>
          <p style={{ margin: '0 0 28px', fontSize: 13, color: 'var(--college-muted)' }}>
            {application.city || '—'} · {type || '—'} · {enrollment != null ? `${fmtCount(enrollment)} undergraduates` : '— undergraduates'}
          </p>

          {/* Dark plan/deadline banner */}
          <div
            style={{
              background: 'var(--college-ink)',
              color: 'var(--college-panel)',
              padding: '14px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              marginBottom: 36,
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
                padding: '3px 10px', borderRadius: 999, background: 'var(--college-panel)', color: 'var(--college-ink)',
                whiteSpace: 'nowrap',
              }}
            >
              {planShort}
            </span>
            <span style={{ fontSize: 14, opacity: 0.9 }}>
              {planLabel} — <span className="font-mono-editorial">{dateShort(application.deadline)}</span>
            </span>
            <span
              className="font-serif-editorial"
              style={{ marginLeft: 'auto', fontSize: 30, fontStyle: 'italic', color: 'var(--college-gold)', whiteSpace: 'nowrap' }}
            >
              {application.days} <span style={{ fontSize: 16 }}>days</span>
            </span>
          </div>

          {/* SAT band */}
          {isUS && (
            <div style={{ marginBottom: 40 }}>
              <p className="label-editorial" style={{ margin: 0 }}>SAT — their middle 50% vs you</p>
              {stats.sat75 != null ? (
                <>
                  <ScoreBar
                    min={1150}
                    max={1600}
                    ticks={[1150, 1300, 1450, 1600]}
                    rangeLow={stats.sat25}
                    rangeHigh={stats.sat75}
                    marks={profile?.satComposite != null
                      ? [{ value: profile.satComposite, label: `You — ${profile.satComposite}`, color: 'var(--college-red)' }]
                      : []}
                  />
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--college-muted)' }}>
                    middle 50%: {stats.sat25 ?? '—'}–{stats.sat75}
                  </p>
                  {profile?.satComposite == null && (
                    <p style={{ margin: '6px 0 0', fontSize: 12 }}>
                      <Link to="/college/profile" style={{ color: 'var(--college-ink-soft)' }}>Add your SAT score</Link> to compare.
                    </p>
                  )}
                </>
              ) : (
                <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--college-muted)' }}>
                  SAT range not available for this school.
                </p>
              )}
            </div>
          )}

          {/* IB band */}
          {showIBBand && (
            <div style={{ marginBottom: 40 }}>
              <p className="label-editorial" style={{ margin: 0 }}>IB — standard offer vs your predicted</p>
              <ScoreBar
                min={30}
                max={45}
                ticks={[30, 35, 40, 45]}
                marks={[
                  { value: stats.ibOffer, label: `Offer — ${stats.ibOffer}`, color: 'var(--college-ink-soft)' },
                  ...(profile?.ibPredicted != null
                    ? [{ value: profile.ibPredicted, label: `You — ${profile.ibPredicted}`, color: 'var(--college-red)' }]
                    : []),
                ]}
              />
              {profile?.ibPredicted == null && (
                <p style={{ margin: '6px 0 0', fontSize: 12 }}>
                  <Link to="/college/profile" style={{ color: 'var(--college-ink-soft)' }}>Add your predicted IB score</Link> to compare.
                </p>
              )}
            </div>
          )}

          {/* Stats grid */}
          <div
            style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1,
              background: 'var(--college-border)', marginBottom: 36,
            }}
          >
            <StatCell label="Admit rate" value={fmtPercent(stats.admitRate)} />
            <StatCell
              label={isUS ? 'SAT mid 50%' : 'IB offer'}
              value={isUS
                ? (stats.sat25 != null && stats.sat75 != null ? `${stats.sat25}–${stats.sat75}` : '—')
                : (stats.ibOffer ?? '—')}
            />
            <StatCell label="Cost per year" value={fmtMoneyShort(sd.tuitionOutOfState ?? sd.tuitionInState ?? manual.costPerYear)} />
            <StatCell label="Undergrads" value={fmtCount(enrollment)} />
          </div>

          {/* Fit read card */}
          <div style={{ border: '1px solid var(--college-ink)', background: 'var(--college-panel)', padding: '24px 28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
              <p className="label-editorial" style={{ margin: 0 }}>Fit read — Gemini</p>
              <FitBadge fit={application.fit} fitColor={application.fitColor} fitBg={application.fitBg} />
            </div>

            {application.aiFitRead && !application.aiFitReadStale ? (
              <>
                <p className="font-serif-editorial" style={{ fontSize: 19, fontStyle: 'italic', lineHeight: 1.5, margin: '0 0 12px' }}>
                  {application.aiFitRead}
                </p>
                <button onClick={() => handleGenerateFitRead(true)} disabled={fitReadLoading} style={linkButtonStyle}>
                  {fitReadLoading ? 'Regenerating…' : '↻ regenerate'}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => handleGenerateFitRead(false)} disabled={fitReadLoading} style={primaryButtonStyle}>
                  {fitReadLoading && <span className="college-spinner" />}
                  {fitReadLoading ? 'Generating…' : 'Generate fit read'}
                </button>
                {fitReadError && (
                  <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--college-red)' }}>
                    {fitReadError.type === 'profile' ? (
                      <>
                        Set your SAT/IB numbers on your{' '}
                        <Link to="/college/profile" style={{ color: 'var(--college-red)', textDecoration: 'underline' }}>Profile</Link> first.
                      </>
                    ) : fitReadError.type === 'gemini' ? (
                      "Gemini isn't configured on the server yet."
                    ) : (
                      fitReadError.message || 'Failed to generate fit read.'
                    )}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* ─── RIGHT RAIL ──────────────────────────────────── */}
        <div>
          <RailSection title="Application status">
            <div>
              {STAGES.map((s, i) => (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                  <span
                    style={{
                      width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                      background: i <= currentStageIndex ? 'var(--college-ink)' : 'transparent',
                      border: '1px solid var(--college-ink)',
                    }}
                  />
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: i === currentStageIndex ? 700 : 400,
                      color: i === currentStageIndex ? 'var(--college-ink)' : 'var(--college-muted)',
                    }}
                  >
                    {s.label}
                  </span>
                </div>
              ))}
              <button
                onClick={handleAdvanceStage}
                disabled={advancing}
                style={{ ...linkButtonStyle, marginTop: 10, color: 'var(--college-red)', textDecoration: 'none' }}
              >
                {advancing ? 'Updating…' : 'advance status →'}
              </button>
            </div>
          </RailSection>

          <RailSection title={`Essays — ${doneEssays}/${essays.length}`}>
            {essays.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--college-muted)', margin: 0 }}>
                No essays linked yet — <Link to="/college/essays" style={{ color: 'var(--college-ink)' }}>link one</Link>.
              </p>
            ) : (
              <div>
                {essays.map((e) => {
                  const meta = essayStatusMeta(e.status);
                  return (
                    <div
                      key={e.id}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                        padding: '8px 0', borderBottom: '1px solid var(--college-border-soft)',
                      }}
                    >
                      <Link
                        to={`/college/essays/${e.id}`}
                        style={{
                          fontSize: 13, color: 'var(--college-ink)', textDecoration: 'none', minWidth: 0,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {e.title || 'Untitled essay'}
                      </Link>
                      <span
                        style={{
                          fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
                          color: meta.color, whiteSpace: 'nowrap',
                        }}
                      >
                        {meta.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </RailSection>

          <RailSection title="From College Visits">
            {latestVisit ? (
              <div>
                <p className="font-mono-editorial" style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--college-muted)' }}>
                  {dateShort(latestVisit.visitDate)} · {VISIT_TYPE_LABELS[latestVisit.visitType] || latestVisit.visitType}
                </p>
                {latestVisit.overallRating != null && (
                  <p style={{ margin: '0 0 10px', fontSize: 14, color: 'var(--college-gold)', letterSpacing: 2 }}>
                    {'★'.repeat(latestVisit.overallRating)}{'☆'.repeat(Math.max(0, 5 - latestVisit.overallRating))}
                  </p>
                )}
                {(latestVisit.firstImpression || latestVisit.generalNotes) && (
                  <p className="font-serif-editorial" style={{ fontStyle: 'italic', fontSize: 15, lineHeight: 1.5, margin: 0, color: 'var(--college-ink-soft)' }}>
                    “{latestVisit.firstImpression || latestVisit.generalNotes}”
                  </p>
                )}
                <Link to="/college-visits" style={{ display: 'inline-block', marginTop: 12, fontSize: 12, color: 'var(--college-ink-soft)' }}>
                  View in College Visits →
                </Link>
              </div>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--college-muted)', margin: 0 }}>
                No visit logged yet — <Link to="/college-visits" style={{ color: 'var(--college-ink)' }}>add one in College Visits</Link>.
              </p>
            )}
          </RailSection>

          <RailSection title="Cost and aid">
            {costRows.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--college-muted)', margin: 0 }}>Not available</p>
            ) : (
              <div>
                {costRows.map((row) => (
                  <div
                    key={row.label}
                    style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--college-border-soft)' }}
                  >
                    <span style={{ fontSize: 13, color: 'var(--college-muted)' }}>{row.label}</span>
                    <span className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-ink)', textAlign: 'right' }}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </RailSection>
        </div>
      </div>
    </div>
  );
}
